import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { isLocalRequest } from '../helpers/url-filter.helper';

/**
 * Header used to correlate a single user-perceived action with everything
 * the server logs while servicing it.
 *
 * The exact spelling matches what the BE expects (case-insensitive on the
 * wire, but we use the canonical form here so it's grep-able in source) —
 * see ``mona/src/python/projects/api/request_context.py::REQUEST_ID_HEADER``.
 */
export const REQUEST_ID_HEADER = 'X-Request-Id';

/**
 * Per-HTTP-request correlation id.
 *
 * Why this exists
 * ---------------
 * The mona BE has a fully-wired ``RequestContextMiddleware`` that reads
 * an inbound ``X-Request-Id``, mints a uuid4 fallback if missing, and
 * stamps the resulting id onto every log record produced while handling
 * the request (``rid=...`` column in the format string). Without an
 * FE-supplied header, every request gets a fresh server-side uuid that
 * has no link back to the terminal action that triggered it — so an
 * operator looking at a slow ``GET /pdms/...`` line in VictoriaLogs has
 * no way to tie it to "Sarah-the-nurse pressed Save at 14:02".
 *
 * What this interceptor does
 * --------------------------
 * Generates a fresh uuid4 per outgoing HTTP request and attaches it as
 * ``X-Request-Id``. The BE echoes the same id back on the response, so
 * the network-diagnostics writer (see
 * ``apps/mona-terminal/src/app/app.ts::registerNetworkDiagnostics``)
 * captures both halves on disk and operators can grep the same uuid
 * across:
 *
 *   1. ``mona-network-diagnostics.jsonl`` (terminal-local trace),
 *   2. NGINX ingress access log (``$http_x_request_id`` is logged once
 *      the chart MR adds it to ``log-format``),
 *   3. BE app log (``rid=<uuid>`` columns are already present today).
 *
 * Caveats
 * -------
 * **HTTP only.** The browser ``WebSocket`` constructor doesn't accept
 *   custom request headers, so WS upgrade frames cannot carry this id
 *   from the renderer. WS-side correlation is out of scope for Phase 1
 *   of OBSERVABILITY_FE_BE_CORRELATION.md.
 * **Pre-existing header wins.** If a caller already attached an
 *   ``X-Request-Id`` (e.g. a future retry interceptor wanting to keep
 *   the original id across attempts) we leave it alone — overwriting
 *   would defeat the very correlation we're trying to enable.
 * **Local asset requests are skipped.** ``GET assets/...`` is served
 *   from the Electron bundle / dev server with no BE involvement, so
 *   tagging it would just bloat headers for no observability gain.
 *   Every other request — including third-party calls — gets the
 *   header; ``X-Request-Id`` is purely identifying and has no effect
 *   on server behaviour.
 */
@Injectable()
export class RequestIdInterceptor implements HttpInterceptor {
    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        if (isLocalRequest(request)) {
            return next.handle(request);
        }
        if (request.headers.has(REQUEST_ID_HEADER)) {
            return next.handle(request);
        }
        return next.handle(
            request.clone({
                setHeaders: { [REQUEST_ID_HEADER]: RequestIdInterceptor.generateRequestId() },
            }),
        );
    }

    /**
     * uuid4 generator.
     *
     * Uses the platform crypto API when available (Electron's Chromium,
     * Node 18+, and modern jsdom all expose ``crypto.randomUUID``). Falls
     * back to a Math.random-based v4 shape only on the truly ancient
     * runtimes that don't — primarily to keep test setups (very old
     * jsdom) running, since production Electron always has the API.
     */
    private static generateRequestId(): string {
        const cryptoApi: Crypto | undefined =
            typeof globalThis !== 'undefined' ? (globalThis as { crypto?: Crypto }).crypto : undefined;
        if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
            return cryptoApi.randomUUID();
        }
        return RequestIdInterceptor.fallbackUuidV4();
    }

    private static fallbackUuidV4(): string {
        // RFC 4122 v4: 32 hex chars in 8-4-4-4-12 layout, with bits 49-52
        // forced to 0100 (version 4) and bits 65-66 to 10 (variant 1).
        const bytes = new Array<number>(16);
        for (let i = 0; i < 16; i++) {
            bytes[i] = Math.floor(Math.random() * 256);
        }
        bytes[6] = (bytes[6] & 0x0f) | 0x40;
        bytes[8] = (bytes[8] & 0x3f) | 0x80;
        const hex = bytes.map(b => b.toString(16).padStart(2, '0')).join('');
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
}
